import React, { useEffect, useState } from "react";
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, Grid, IconButton,
    MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";

import {
    getSucursales, getCategorias, getAdvertencias, editarItem,
} from "../services/audit.service";

/**
 * AuditEditModal
 *
 * Props:
 *   open      {bool}
 *   onClose   {fn}
 *   onSuccess {fn}   — se llama después de guardar
 *   item      {Object} — datos actuales del ítem
 *   username  {string}
 */
export default function AuditEditModal({ open, onClose, onSuccess, item, username }) {
    const [form, setForm] = useState({});
    const [sucursales, setSucursales] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [advertencias, setAdvertencias] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});

    // Cargar maestros y poblar form cuando abre
    useEffect(() => {
        if (!open || !item) return;

        // Poblar form con datos actuales del ítem
        setForm({
            titulo: item.titulo || "",
            descripcion: item.descripcion || "",
            categoria: item.categoria || "",
            advertencia_id: item.advertencia_id ? String(item.advertencia_id) : "",
            prioridad: item.prioridad || "MEDIA",
            sucursal_id: item.sucursal_id || "",
            fecha_deteccion: item.fecha_deteccion
                ? item.fecha_deteccion.split("T")[0] : "",
            fecha_requerida: item.fecha_requerida
                ? item.fecha_requerida.split("T")[0] : "",
        });
        setErrors({});
        setError(null);

        // Cargar maestros
        getSucursales().then(setSucursales).catch(() => { });
        getCategorias().then(setCategorias).catch(() => { });
        getAdvertencias().then(setAdvertencias).catch(() => { });
    }, [open, item]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: "" }));
    };

    const validar = () => {
        const e = {};
        if (!form.titulo?.trim()) e.titulo = "Obligatorio";
        if (!form.descripcion?.trim()) e.descripcion = "Obligatorio";
        if (!form.sucursal_id) e.sucursal_id = "Seleccioná una sucursal";
        if (!form.categoria) e.categoria = "Seleccioná una categoría";
        if (!form.advertencia_id) e.advertencia_id = "Seleccioná un tipo de advertencia";
        if (!form.fecha_deteccion) e.fecha_deteccion = "Obligatorio";
        if (!form.fecha_requerida) e.fecha_requerida = "Obligatorio";
        if (form.fecha_requerida && form.fecha_deteccion &&
            form.fecha_requerida < form.fecha_deteccion)
            e.fecha_requerida = "No puede ser anterior a la detección";
        setErrors(e);
        return !Object.keys(e).length;
    };

    const handleGuardar = async () => {
        if (!validar()) return;
        setGuardando(true);
        setError(null);
        try {
            //await editarItem(item.id, { ...form, username });
            await editarItem(item.id, { ...form, username, advertencia_id: form.advertencia_id || null });
            onClose();
            onSuccess?.();
        } catch (e) {
            setError(e?.response?.data?.error || "Error al guardar. Intentá nuevamente.");
        } finally {
            setGuardando(false);
        }
    };

    const handleClose = () => {
        if (guardando) return;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" gap={1}>
                        <EditIcon color="primary" sx={{ fontSize: 18 }} />
                        <Typography variant="h6" fontWeight={700}>
                            Editar ítem #{item?.id}
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={handleClose} disabled={guardando}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* Título */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Título *" name="titulo"
                            value={form.titulo || ""} onChange={handleChange}
                            error={!!errors.titulo} helperText={errors.titulo}
                            inputProps={{ maxLength: 200 }}
                        />
                    </Grid>

                    {/* Descripción */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth multiline rows={3}
                            label="Descripción / Observación *" name="descripcion"
                            value={form.descripcion || ""} onChange={handleChange}
                            error={!!errors.descripcion} helperText={errors.descripcion}
                        />
                    </Grid>

                    {/* Categoría */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth label="Categoría *" name="categoria"
                            value={form.categoria || ""} onChange={handleChange}
                            error={!!errors.categoria} helperText={errors.categoria}
                        >
                            {categorias.map(c => (
                                <MenuItem key={c.id} value={c.nombre}>{c.nombre}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Advertencia */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth label="Tipo de advertencia *" name="advertencia_id"
                            value={form.advertencia_id || ""} onChange={handleChange}
                            error={!!errors.advertencia_id} helperText={errors.advertencia_id}
                        >
                            {advertencias.map(a => (
                                <MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Prioridad */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth label="Prioridad *" name="prioridad"
                            value={form.prioridad || "MEDIA"} onChange={handleChange}
                        >
                            <MenuItem value="BAJA">🟢  Baja</MenuItem>
                            <MenuItem value="MEDIA">🟡  Media</MenuItem>
                            <MenuItem value="ALTA">🟠  Alta</MenuItem>
                            <MenuItem value="CRITICA">🔴  Crítica</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Sucursal */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth label="Sucursal *" name="sucursal_id"
                            value={form.sucursal_id || ""} onChange={handleChange}
                            error={!!errors.sucursal_id} helperText={errors.sucursal_id}
                        >
                            {sucursales.map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Fecha detección */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth type="date"
                            label="Fecha de detección *" name="fecha_deteccion"
                            value={form.fecha_deteccion || ""} onChange={handleChange}
                            error={!!errors.fecha_deteccion} helperText={errors.fecha_deteccion}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Fecha requerida */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth type="date"
                            label="Fecha límite de solución *" name="fecha_requerida"
                            value={form.fecha_requerida || ""} onChange={handleChange}
                            error={!!errors.fecha_requerida} helperText={errors.fecha_requerida}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: form.fecha_deteccion }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" color="inherit"
                    onClick={handleClose} disabled={guardando}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    startIcon={guardando
                        ? <CircularProgress size={14} color="inherit" />
                        : <SaveIcon />}
                    onClick={handleGuardar}
                    disabled={guardando}
                >
                    {guardando ? "Guardando..." : "Guardar cambios"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}